import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { extractYoutubeId, slugifyText } from "@/lib/utils-vr";
import { FileDropzone } from "@/components/vr/FileDropzone";
import { X, Check } from "lucide-react";

/** Renders a single field based on its schema descriptor. */
export function FieldRenderer({ field, value, onChange, form }) {
  const [classes, setClasses] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [chapters, setChapters] = useState(null);
  const [mcqs, setMcqs] = useState(null);

  useEffect(() => {
    if (["class-select", "class-multiselect"].includes(field.type) && !classes) {
      api.get("/classes").then((r) => setClasses(r.data));
    }
    if (field.type === "subject-select" && form?.[field.dependsOn]) {
      api.get(`/subjects?class_slug=${form[field.dependsOn]}`).then((r) => setSubjects(r.data));
    }
    if (field.type === "chapter-select" && form?.class_slug && form?.subject_slug) {
      api.get(`/chapters?class_slug=${form.class_slug}&subject_slug=${form.subject_slug}`).then((r) => setChapters(r.data));
    }
    if (field.type === "mcq-multiselect" && form?.class_slug) {
      const s = form.subject_slug ? `&subject_slug=${form.subject_slug}` : "";
      api.get(`/mcqs?class_slug=${form.class_slug}${s}&limit=500`).then((r) => setMcqs(r.data));
    }
  }, [field.type, field.dependsOn, form?.class_slug, form?.subject_slug]); // eslint-disable-line

  const set = (v) => onChange(v);
  const testid = `f-${field.key}`;

  if (field.type === "image-upload") {
    return <FileDropzone kind="image" value={value} onChange={set} testid={testid} />;
  }
  if (field.type === "pdf-upload") {
    return <FileDropzone kind="pdf" value={value} onChange={set} testid={testid} />;
  }

  if (field.type === "text" || field.type === "slug") {
    return (
      <input
        type="text"
        value={value ?? ""}
        readOnly={field.readOnly}
        placeholder={field.readOnly ? "(auto-filled)" : ""}
        onChange={(e) => set(e.target.value)}
        onBlur={() => {
          if (field.type === "slug" && field.from && !value && form?.[field.from]) {
            set(slugifyText(form[field.from]));
          }
        }}
        className={`w-full h-10 px-3 border border-slate-200 rounded text-sm ${field.readOnly ? "bg-slate-50 text-slate-500" : ""}`}
        data-testid={testid}
      />
    );
  }
  if (field.type === "textarea") {
    return <textarea rows={field.rows || 3} value={value ?? ""} onChange={(e) => set(e.target.value)} className="w-full p-2 border border-slate-200 rounded text-sm" data-testid={testid} />;
  }
  if (field.type === "number") {
    return <input type="number" value={value ?? ""} onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))} className="w-full h-10 px-3 border border-slate-200 rounded text-sm" data-testid={testid} />;
  }
  if (field.type === "bool") {
    return (
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} data-testid={testid} /> {field.label}
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={value ?? field.default ?? ""} onChange={(e) => set(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded text-sm bg-white" data-testid={testid}>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "class-select") {
    return (
      <select value={value ?? ""} onChange={(e) => set(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded text-sm bg-white" data-testid={testid}>
        <option value="">— choose class —</option>
        {(classes || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
    );
  }
  if (field.type === "class-multiselect") {
    const arr = value || [];
    return (
      <div className="flex flex-wrap gap-2 border border-slate-200 p-2 rounded" data-testid={testid}>
        {(classes || []).map((c) => (
          <label key={c.slug} className="inline-flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded">
            <input type="checkbox" checked={arr.includes(c.slug)} onChange={(e) => set(e.target.checked ? [...arr, c.slug] : arr.filter((x) => x !== c.slug))} />
            {c.name}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "subject-select") {
    return (
      <select value={value ?? ""} onChange={(e) => set(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded text-sm bg-white" data-testid={testid}>
        <option value="">— choose subject —</option>
        {(subjects || []).map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
      </select>
    );
  }
  if (field.type === "chapter-select") {
    return (
      <select value={value ?? ""} onChange={(e) => set(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded text-sm bg-white" data-testid={testid}>
        <option value="">— none —</option>
        {(chapters || []).map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
      </select>
    );
  }
  if (field.type === "list") {
    return (
      <textarea rows={5} value={(value || []).join("\n")} onChange={(e) => set(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="w-full p-2 border border-slate-200 rounded text-sm font-mono" data-testid={testid} />
    );
  }
  if (field.type === "kv-list") {
    const [ka, kb] = field.keys;
    const arr = value || [];
    return (
      <div className="space-y-2" data-testid={testid}>
        {arr.map((row, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input placeholder={ka} value={row[ka] || ""} onChange={(e) => set(arr.map((r, j) => j === i ? { ...r, [ka]: e.target.value } : r))} className="w-1/3 h-9 px-2 border border-slate-200 rounded text-sm" />
            <textarea rows={2} placeholder={kb} value={row[kb] || ""} onChange={(e) => set(arr.map((r, j) => j === i ? { ...r, [kb]: e.target.value } : r))} className="flex-1 p-2 border border-slate-200 rounded text-sm" />
            <button type="button" onClick={() => set(arr.filter((_, j) => j !== i))} className="h-9 w-9 rounded border border-slate-200 text-red-600"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => set([...arr, { [ka]: "", [kb]: "" }])} className="text-xs font-semibold text-[#1E3A8A]">+ Add row</button>
      </div>
    );
  }
  if (field.type === "options-4") {
    const opts = value && value.length ? value : [
      { text: "", is_correct: true }, { text: "", is_correct: false },
      { text: "", is_correct: false }, { text: "", is_correct: false },
    ];
    return (
      <div className="space-y-2" data-testid={testid}>
        {opts.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="radio" name={`correct-${field.key}`} checked={!!o.is_correct} onChange={() => set(opts.map((oo, j) => ({ ...oo, is_correct: j === i })))} data-testid={`f-opt-correct-${i}`} />
            <input placeholder={`Option ${String.fromCharCode(65 + i)}`} value={o.text} onChange={(e) => set(opts.map((oo, j) => j === i ? { ...oo, text: e.target.value } : oo))} className="flex-1 h-9 px-2 border border-slate-200 rounded text-sm" data-testid={`f-opt-text-${i}`} />
          </div>
        ))}
        <p className="text-xs text-slate-500">Select the radio next to the correct option.</p>
      </div>
    );
  }
  if (field.type === "youtube-url" || field.type === "youtube-url-full") {
    const ext = extractYoutubeId(value);
    return (
      <div>
        <input type="text" value={value ?? ""} onChange={(e) => {
          const v = e.target.value;
          const id = extractYoutubeId(v);
          if (field.autoSetField && field.onFormChange) {
            field.onFormChange({ [field.key]: v, [field.autoSetField]: id });
          } else {
            set(v);
          }
        }} placeholder="Paste YouTube URL or 11-char video ID" className="w-full h-10 px-3 border border-slate-200 rounded text-sm" data-testid={testid} />
        {value && (
          <div className={`mt-1 text-xs ${ext ? "text-emerald-700" : "text-red-600"}`} data-testid={`f-yt-status-${field.key}`}>
            {ext ? <><Check size={12} className="inline" /> Extracted video ID: <code className="font-mono">{ext}</code></> : "Not a valid YouTube URL"}
          </div>
        )}
      </div>
    );
  }
  if (field.type === "mcq-multiselect") {
    const arr = value || [];
    return (
      <div className="max-h-64 overflow-auto border border-slate-200 rounded p-2 space-y-1" data-testid={testid}>
        {(mcqs || []).length === 0 && <div className="text-xs text-slate-500">Select class + subject above to load MCQs.</div>}
        {(mcqs || []).map((m) => (
          <label key={m.id} className="flex items-start gap-2 text-sm py-1 border-b border-slate-100">
            <input type="checkbox" checked={arr.includes(m.id)} onChange={(e) => onChange(e.target.checked ? [...arr, m.id] : arr.filter((x) => x !== m.id))} />
            <span className="flex-1">{m.question}</span>
          </label>
        ))}
      </div>
    );
  }
  return <div className="text-xs text-red-600">Unknown field type: {field.type}</div>;
}

export function validateForm(fields, form) {
  const errors = {};
  for (const f of fields) {
    const v = form?.[f.key];
    const missing = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    if (f.required && missing) {
      errors[f.key] = `${f.label} is required`;
      continue;
    }
    if ((f.type === "youtube-url" || f.type === "youtube-url-full") && v) {
      if (!extractYoutubeId(v)) errors[f.key] = "Invalid YouTube URL";
    }
    if (f.type === "options-4" && (f.required || (Array.isArray(v) && v.length))) {
      const opts = v || [];
      if (opts.length < 2 || opts.some((o) => !o.text?.trim())) errors[f.key] = "All options must have text";
      else if (!opts.some((o) => o.is_correct)) errors[f.key] = "Mark one option as correct";
    }
  }
  return errors;
}

export function applyDefaults(fields, form = {}) {
  const out = { ...form };
  for (const f of fields) {
    if (out[f.key] === undefined && f.default !== undefined) out[f.key] = f.default;
  }
  return out;
}

export function prepareSubmit(fields, form) {
  const out = {};
  for (const f of fields) {
    let v = form[f.key];
    if (v === undefined || v === null) continue;
    // Normalize youtube-url: prefer extracted id when it's the id-only field
    if (f.type === "youtube-url" && v) v = extractYoutubeId(v) || v;
    out[f.key] = v;
  }
  return out;
}
